import { createServerFn } from "@tanstack/react-start";

import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { TOPUP_PRICE_ID } from "@/lib/ai-credits";
import {
  BUSINESS_PRICE_ID,
  MIN_SEATS,
  PREMIUM_PRICE_ID,
  PRO_PRICE_ID,
  TEAM_PRICE_ID,
  TIER_BY_PRICE_ID,
  TIER_RANK,
  subscriptionTier,
} from "@/lib/entitlements";
import { createStripeClient, getStripeErrorMessage, type StripeEnv } from "@/lib/stripe.server";

const ALLOWED_PRICE_IDS = [PRO_PRICE_ID, PREMIUM_PRICE_ID, TEAM_PRICE_ID, BUSINESS_PRICE_ID];

/** Seat-based plans bill per user, so the checkout quantity is the seat count. */
function seatsFor(priceId: string, requested: number | undefined): number {
  const tier = TIER_BY_PRICE_ID[priceId];
  if (tier !== "team" && tier !== "business") return 1;
  const min = MIN_SEATS[tier];
  const seats = Math.floor(requested ?? min);
  if (!Number.isFinite(seats) || seats < min) return min;
  return Math.min(500, seats);
}

type CheckoutResult = { clientSecret: string } | { error: string };
type PortalResult = { url: string } | { error: string };

export const createSubscriptionCheckout = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    (data: {
      priceId: string;
      seats?: number;
      returnUrl: string;
      environment: StripeEnv;
    }) => {
      if (!ALLOWED_PRICE_IDS.includes(data.priceId)) throw new Error("Unknown plan");
      if (data.environment !== "sandbox" && data.environment !== "live") {
        throw new Error("Invalid environment");
      }
      return { ...data, seats: seatsFor(data.priceId, data.seats) };
    },
  )
  .handler(async ({ data, context }): Promise<CheckoutResult> => {
    const { supabase, userId } = context;
    const seats = data.seats ?? 1;

    const { data: row } = await supabase
      .from("subscriptions")
      .select("billing_email, stripe_customer_id")
      .eq("user_id", userId)
      .eq("environment", data.environment)
      .maybeSingle();

    const { data: anyRow } = row?.billing_email
      ? { data: row }
      : await supabase
          .from("subscriptions")
          .select("billing_email, stripe_customer_id")
          .not("billing_email", "is", null)
          .limit(1)
          .maybeSingle();

    const email = row?.billing_email ?? anyRow?.billing_email ?? null;
    if (!email) return { error: "Add a billing email on the Account page first." };

    try {
      const stripe = createStripeClient(data.environment);

      const prices = await stripe.prices.list({ lookup_keys: [data.priceId] });
      if (!prices.data.length) return { error: "That plan is not available yet." };
      const price = prices.data[0]!;

      let customerId = row?.stripe_customer_id ?? null;
      if (!customerId) {
        const found = await stripe.customers.search({
          query: `metadata['userId']:'${userId}'`,
          limit: 1,
        });
        customerId = found.data[0]?.id ?? null;
      }
      if (!customerId) {
        const created = await stripe.customers.create({
          email,
          metadata: { userId },
        });
        customerId = created.id;
      }

      const session = await stripe.checkout.sessions.create({
        line_items: [{ price: price.id, quantity: seats }],
        mode: "subscription",
        ui_mode: "embedded_page",
        return_url: data.returnUrl,
        customer: customerId,
        metadata: { userId },
        subscription_data: { metadata: { userId } },
        managed_payments: { enabled: true },
      } as never);

      await supabase
        .from("subscriptions")
        .upsert(
          {
            user_id: userId,
            environment: data.environment,
            billing_email: email,
            stripe_customer_id: customerId,
            seats,
          },
          { onConflict: "user_id,environment" },
        );

      return { clientSecret: session.client_secret ?? "" };
    } catch (error) {
      return { error: getStripeErrorMessage(error) };
    }
  });

export const createBillingPortalSession = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { returnUrl?: string; environment: StripeEnv }) => data)
  .handler(async ({ data, context }): Promise<PortalResult> => {
    const { supabase, userId } = context;

    const { data: row } = await supabase
      .from("subscriptions")
      .select("stripe_customer_id")
      .eq("user_id", userId)
      .eq("environment", data.environment)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (!row?.stripe_customer_id) return { error: "No billing account yet." };

    try {
      const stripe = createStripeClient(data.environment);
      const portal = await stripe.billingPortal.sessions.create({
        customer: row.stripe_customer_id,
        ...(data.returnUrl && { return_url: data.returnUrl }),
      });
      return { url: portal.url };
    } catch (error) {
      return { error: getStripeErrorMessage(error) };
    }
  });

export const saveBillingEmail = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { email: string; environment: StripeEnv }) => {
    const email = data.email.trim().toLowerCase();
    if (email.length > 255 || !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
      throw new Error("Enter a valid email address");
    }
    return { email, environment: data.environment };
  })
  .handler(async ({ data, context }): Promise<{ ok: true } | { error: string }> => {
    const { supabase, userId } = context;
    const { error } = await supabase.from("subscriptions").upsert(
      {
        user_id: userId,
        environment: data.environment,
        billing_email: data.email,
      },
      { onConflict: "user_id,environment" },
    );
    if (error) return { error: error.message };
    return { ok: true };
  });

export const createTopupCheckout = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { returnUrl: string; environment: StripeEnv }) => {
    if (data.environment !== "sandbox" && data.environment !== "live") {
      throw new Error("Invalid environment");
    }
    return data;
  })
  .handler(async ({ data, context }): Promise<CheckoutResult> => {
    const { supabase, userId } = context;

    const { data: row } = await supabase
      .from("subscriptions")
      .select("billing_email, stripe_customer_id, tier, status, current_period_end")
      .eq("user_id", userId)
      .eq("environment", data.environment)
      .maybeSingle();

    if (!row?.billing_email) {
      return { error: "Add a billing email on the Account page first." };
    }

    // Top-ups are a paid-plan add-on, so a free account cannot buy AI credits.
    const tier = subscriptionTier(row);
    if (TIER_RANK[tier] < TIER_RANK.pro) {
      return { error: "Top-ups are available on Pro and Premium. Upgrade your plan first." };
    }


    try {
      const stripe = createStripeClient(data.environment);
      const prices = await stripe.prices.list({ lookup_keys: [TOPUP_PRICE_ID] });
      if (!prices.data.length) return { error: "Top-ups are not available yet." };

      let customerId = row.stripe_customer_id ?? null;
      if (!customerId) {
        const created = await stripe.customers.create({
          email: row.billing_email,
          metadata: { userId },
        });
        customerId = created.id;
        await supabase
          .from("subscriptions")
          .update({ stripe_customer_id: customerId })
          .eq("user_id", userId)
          .eq("environment", data.environment);
      }

      const session = await stripe.checkout.sessions.create({
        line_items: [{ price: prices.data[0]!.id, quantity: 1 }],
        mode: "payment",
        ui_mode: "embedded_page",
        return_url: data.returnUrl,
        customer: customerId,
        metadata: { userId, kind: "ai_topup" },
        managed_payments: { enabled: true },
      } as never);

      return { clientSecret: session.client_secret ?? "" };
    } catch (error) {
      return { error: getStripeErrorMessage(error) };
    }
  });
